import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { getCompanyStatus, getMyCompany, createCompany, updateCompany } from '../utils/apiServices';
import '../stylesheets/CompanyProfile.css';
import locations from '../utils/colombia.json';

// Opciones para el sistema de negocio (según tu plan)
const businessSystemOptions = [
    { value: 'SALUD_BELLEZA', label: 'Salud/Belleza' },
    { value: 'HOGAR', label: 'Hogar' },
    { value: 'ALIMENTOS', label: 'Alimentos' },
    { value: 'INDUSTRIAS_CREATIVAS', label: 'Industrias creativas' },
    { value: 'MODA', label: 'Moda' },
    { value: 'SERVICIOS', label: 'Servicios' },
    { value: 'OTRO', label: 'Otros' },
];

// Opciones para tipos de alianzas
const allianceTypeOptions = [
    { value: 'PROVEEDURIA', label: 'Proveeduría' },
    { value: 'SERVICIOS', label: 'Servicios' },
    { value: 'COMPRAS_VENTAS_CONJUNTAS', label: 'Para realizar compras y ventas conjuntas' },
    { value: 'COMERCIALIZACION', label: 'Comercialización' },
    { value: 'OTRAS', label: 'Otras (especifique cuáles)' },
];

// Opciones para productos y servicios
const productsServicesOptions = [
    { value: 'ALIMENTOS_PREPARADOS', label: 'Alimentos Preparados' },
    { value: 'SERVICIO_RESTAURANTE', label: 'Servicio de Restaurante y comidas rápidas' },
    { value: 'MANUFACTURA_ROPA', label: 'Manufactura - Prendas de vestir' },
    { value: 'COMERCIALIZACION_ROPA', label: 'Comercialización de Ropa y Calzado' },
    { value: 'ACCESORIOS_BISUTERIA', label: 'Accesorios/Bisutería/Joyería' },
    { value: 'PRODUCTOS_BELLEZA', label: 'Productos y servicios de belleza' },
    { value: 'ARTESANIAS', label: 'Artesanías' },
    { value: 'PRODUCTOS_HOGAR', label: 'Productos de hogar (Muebles, Cuadros, Alfombras, Plantas etc.)' },
    { value: 'SERVICIOS_PUBLICIDAD', label: 'Servicios de Publicidad y diseño' },
    { value: 'SERVICIOS_DECORACION', label: 'Servicios y productos de decoración' },
    { value: 'SERVICIOS_TECNOLOGICOS', label: 'Servicios tecnológicos, informáticos y otros.' },
    { value: 'SERVICIOS_CONTABLES', label: 'Servicios Contables' },
    { value: 'OTROS', label: 'Otros' },
];

// Estado inicial del formulario (vacío) - Nombres de campos según el backend
const initialState = {
    company_name: '',
    company_nit: '',
    company_description: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    main_office_department: '',
    main_office_municipality: '',
    company_system: 'OTRO',
    relevant_info: '',
    what_i_seek: '',
    what_i_offer: '',
    alliance_types: [],
    alliance_types_other: '',
    products_services_offered: [],
    products_services_offered_other: '',
    products_services_seeking: [],
    products_services_seeking_other: '',
};

const CompanyProfile = () => {
    const { user } = useContext(AuthContext);
    const [companyId, setCompanyId] = useState(null);
    // El personal administrativo recorre la plataforma sin empresa propia.
    const isStaffWithoutCompany = Boolean(user?.is_staff) && !companyId;
    const [formData, setFormData] = useState(initialState);
    const [municipalities, setMunicipalities] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', content: '' });

    useEffect(() => {
        const fetchCompanyData = async () => {
            if (!user?.token) return;

            setLoading(true);
            try {
                const status = await getCompanyStatus(user.token);
                
                if (status.has_company) {
                    const companyData = await getMyCompany(user.token);
                    setFormData({
                        company_name: companyData.company_name || '',
                        company_nit: companyData.company_nit || '',
                        company_description: companyData.company_description || '',
                        company_email: companyData.company_email || '',
                        company_phone: companyData.company_phone || '',
                        company_address: companyData.company_address || '',
                        main_office_department: companyData.main_office_department || '',
                        main_office_municipality: companyData.main_office_municipality || '',
                        company_system: companyData.company_system || 'OTRO',
                        relevant_info: companyData.relevant_info || '',
                        what_i_seek: companyData.what_i_seek || '',
                        what_i_offer: companyData.what_i_offer || '',
                        alliance_types: companyData.alliance_types || [],
                        alliance_types_other: companyData.alliance_types_other || '',
                        products_services_offered: companyData.products_services_offered || [],
                        products_services_offered_other: companyData.products_services_offered_other || '',
                        products_services_seeking: companyData.products_services_seeking || [],
                        products_services_seeking_other: companyData.products_services_seeking_other || '',
                    });
                    setCompanyId(companyData.id);
                    
                    // Cargar municipios del departamento si existe
                    if (companyData.main_office_department) {
                        const foundDept = locations.find(d => d.departamento === companyData.main_office_department);
                        setMunicipalities(foundDept ? foundDept.ciudades : []);
                    }
                } else {
                    setFormData(initialState);
                    setCompanyId(null);
                }
            } catch (error) {
                setMessage({ type: 'error', content: 'No se pudo cargar la información de la empresa.' });
            } finally {
                setLoading(false);
            }
        };

        fetchCompanyData();
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDepartmentChange = (e) => {
        const deptName = e.target.value;
        setFormData(prev => ({
            ...prev,
            main_office_department: deptName,
            main_office_municipality: '' // Resetear municipio al cambiar departamento
        }));
        
        // Cargar municipios del departamento seleccionado
        const foundDept = locations.find(d => d.departamento === deptName);
        setMunicipalities(foundDept ? foundDept.ciudades : []);
    };

    const handleCheckboxChange = (fieldName, value) => {
        setFormData(prev => {
            const currentValues = prev[fieldName] || [];
            const isChecked = currentValues.includes(value);
            
            return {
                ...prev,
                [fieldName]: isChecked 
                    ? currentValues.filter(v => v !== value)
                    : [...currentValues, value]
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage({ type: '', content: '' });

        try {
            let response;
            if (companyId) {
                response = await updateCompany(user.token, formData);
                setMessage({ type: 'success', content: '¡Empresa actualizada con éxito!' });
            } else {
                response = await createCompany(user.token, formData);
                setCompanyId(response.id);
                setMessage({ type: 'success', content: '¡Empresa creada con éxito!' });
            }
            setFormData(prev => ({ ...prev, ...response }));

        } catch (error) {
            const errorMsg = 'Error al guardar. Revisa todos los campos. El NIT debe ser único.';
            setMessage({ type: 'error', content: errorMsg });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <div className="profile-page-container profile-loading">Cargando...</div>;
    }

    // El personal administrativo no registra empresa: aparecería en las búsquedas
    // y recomendaciones del resto. El backend también lo rechaza (403).
    if (isStaffWithoutCompany) {
        return (
            <div className="profile-page-container">
                <h1>Perfil de Empresa</h1>
                <div className="profile-notice">
                    <h2>Vista de administrador</h2>
                    <p>
                        El personal administrativo no registra empresa propia. Si lo
                        hiciera, aparecería en las búsquedas y recomendaciones del resto
                        de usuarios.
                    </p>
                    <p>
                        El listado completo de empresas, incluidas las inactivas, está en
                        el <a href="/admin/companies/company/">panel de administración</a>.
                        El buscador del{' '}
                        <a href="/conexiones">Dashboard de Conexiones</a> solo muestra las
                        empresas activas.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-page-container">
            <h1>{companyId ? 'Editar Mi Empresa' : 'Registrar Mi Empresa'}</h1>
            <p className="profile-subtitle">
                {companyId 
                    ? 'Actualiza los datos de tu empresa para mejorar tus recomendaciones de alianzas.' 
                    : 'Completa tu perfil empresarial para comenzar a recibir recomendaciones personalizadas de alianzas.'}
            </p>

            <div className="profile-section">
                <form onSubmit={handleSubmit}>
                    
                    {/* ===== PASO 1: INFORMACIÓN BÁSICA ===== */}
                    <div className="section-header full-width">
                        <span className="section-number">1</span>
                        <div>
                            <h2 className="section-title">📋 Información Básica</h2>
                            <p className="section-description">Datos generales de identificación de tu empresa</p>
                        </div>
                    </div>

                    <div className="section-card">
                        <h3>Identificación</h3>
                        <div className="form-group">
                            <label htmlFor="company_name">Nombre de la Empresa</label>
                            <input 
                                type="text" 
                                id="company_name" 
                                name="company_name" 
                                value={formData.company_name} 
                                onChange={handleChange} 
                                required 
                                placeholder="Ej: Panadería El Buen Sabor" 
                            />
                            <small>Nombre comercial de tu empresa</small>
                        </div>
                        <div className="form-group">
                            <label htmlFor="company_nit">NIT</label>
                            <input 
                                type="text" 
                                id="company_nit" 
                                name="company_nit" 
                                value={formData.company_nit} 
                                onChange={handleChange} 
                                required 
                                placeholder="Ej: 900123456" 
                            />
                            <small>Sin dígito de verificación</small>
                        </div>
                        <div className="form-group">
                            <label htmlFor="company_system">Categoría de tu Empresa</label>
                            <select 
                                id="company_system" 
                                name="company_system" 
                                value={formData.company_system} 
                                onChange={handleChange}
                            >
                                {businessSystemOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <small>Selecciona el sistema al que pertenece tu empresa</small>
                        </div>
                    </div>

                    <div className="section-card">
                        <h3>Descripción</h3>
                        <div className="form-group">
                            <label htmlFor="company_description">¿Qué hace tu empresa?</label>
                            <textarea 
                                id="company_description" 
                                name="company_description" 
                                value={formData.company_description} 
                                onChange={handleChange} 
                                rows="5" 
                                placeholder="Ej: Somos una panadería artesanal que produce pan tradicional y repostería desde hace 15 años, utilizando ingredientes naturales y recetas familiares..."
                            ></textarea>
                            <small>Describe brevemente tu empresa especificando, los tipos de productos y servicios que ofreces, tu público objetivo y qué la hace especial</small>
                        </div>
                    </div>

                    {/* ===== PASO 2: UBICACIÓN Y CONTACTO ===== */}
                    <div className="section-header full-width">
                        <span className="section-number">2</span>
                        <div>
                            <h2 className="section-title">📍 Ubicación y Contacto</h2>
                            <p className="section-description">Dónde te encuentras y cómo pueden contactarte</p>
                        </div>
                    </div>

                    <div className="section-card">
                        <h3>Ubicación</h3>
                        <div className="form-group">
                            <label htmlFor="main_office_department">Departamento</label>
                            <select 
                                id="main_office_department" 
                                name="main_office_department" 
                                value={formData.main_office_department} 
                                onChange={handleDepartmentChange}
                            >
                                <option value="">Seleccione un departamento</option>
                                {locations.map(dept => (
                                    <option key={dept.id} value={dept.departamento}>{dept.departamento}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="main_office_municipality">Municipio</label>
                            <select 
                                id="main_office_municipality" 
                                name="main_office_municipality" 
                                value={formData.main_office_municipality} 
                                onChange={handleChange}
                                disabled={municipalities.length === 0}
                            >
                                <option value="">Seleccione un municipio</option>
                                {municipalities.map(city => (
                                    <option key={city} value={city}>{city}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="company_address">Dirección</label>
                            <input 
                                type="text" 
                                id="company_address" 
                                name="company_address" 
                                value={formData.company_address} 
                                onChange={handleChange} 
                                placeholder="Ej: Calle 10 # 5-20" 
                            />
                            <small>Dirección de tu oficina principal</small>
                        </div>
                    </div>

                    <div className="section-card">
                        <h3>Contacto</h3>
                        <div className="form-group">
                            <label htmlFor="company_email">Correo Electrónico</label>
                            <input 
                                type="email" 
                                id="company_email" 
                                name="company_email" 
                                value={formData.company_email} 
                                onChange={handleChange} 
                                placeholder="Ej: contacto@miempresa.com" 
                            />
                            <small>Correo donde otras empresas podrán contactarte</small>
                        </div>
                        <div className="form-group">
                            <label htmlFor="company_phone">Teléfono</label>
                            <input 
                                type="tel" 
                                id="company_phone" 
                                name="company_phone" 
                                value={formData.company_phone} 
                                onChange={handleChange} 
                                placeholder="Ej: 3001234567" 
                            />
                            <small>Número de contacto (celular o fijo)</small>
                        </div>
                    </div>

                    {/* ===== PASO 3: TIPOS DE ALIANZAS ESPECÍFICAS ===== */}
                    <div className="section-header full-width">
                        <span className="section-number">3</span>
                        <div>
                            <h2 className="section-title">🎯 Tipos de Alianzas de tu Interés</h2>
                            <p className="section-description">Selecciona qué tipos de colaboraciones empresariales te interesan</p>
                        </div>
                    </div>

                    <div className="section-card full-width">
                        <h3>Tipos de Alianzas</h3>
                        <div className="form-group">
                            <label>Selecciona uno o varios tipos de alianzas que te gustaría realizar:</label>
                            <div className="checkbox-group">
                                {allianceTypeOptions.map(option => (
                                    <div key={option.value} className="checkbox-item">
                                        <input
                                            type="checkbox"
                                            id={`alliance_${option.value}`}
                                            checked={formData.alliance_types.includes(option.value)}
                                            onChange={() => handleCheckboxChange('alliance_types', option.value)}
                                        />
                                        <label htmlFor={`alliance_${option.value}`}>{option.label}</label>
                                    </div>
                                ))}
                            </div>
                            {formData.alliance_types.includes('OTRAS') && (
                                <div className="form-group conditional-field">
                                    <label htmlFor="alliance_types_other">¿Qué otros tipos de alianzas te interesan?</label>
                                    <input
                                        type="text"
                                        id="alliance_types_other"
                                        name="alliance_types_other"
                                        value={formData.alliance_types_other}
                                        onChange={handleChange}
                                        placeholder="Ej: Alianzas para exportación, joint ventures, franquicias..."
                                        maxLength={100}
                                    />
                                    <small>{formData.alliance_types_other?.length || 0}/100 caracteres</small>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ===== PASO 4: PRODUCTOS Y SERVICIOS ESPECÍFICOS ===== */}
                    <div className="section-header full-width">
                        <span className="section-number">4</span>
                        <div>
                            <h2 className="section-title">📦 Productos y Servicios Específicos</h2>
                            <p className="section-description">Ayúdanos a encontrar las alianzas perfectas para ti</p>
                        </div>
                    </div>

                    <div className="section-card full-width">
                        <h3>🔍 ¿Qué Productos/Servicios Busco Encontrar?</h3>
                        <div className="form-group">
                            <label>Selecciona los productos o servicios que te interesa encontrar en este espacio:</label>
                            <div className="checkbox-group">
                                {productsServicesOptions.map(option => (
                                    <div key={option.value} className="checkbox-item">
                                        <input
                                            type="checkbox"
                                            id={`seeking_${option.value}`}
                                            checked={formData.products_services_seeking.includes(option.value)}
                                            onChange={() => handleCheckboxChange('products_services_seeking', option.value)}
                                        />
                                        <label htmlFor={`seeking_${option.value}`}>{option.label}</label>
                                    </div>
                                ))}
                            </div>
                            {formData.products_services_seeking.includes('OTROS') && (
                                <div className="form-group conditional-field">
                                    <label htmlFor="products_services_seeking_other">Especifica otros productos/servicios que buscas:</label>
                                    <input
                                        type="text"
                                        id="products_services_seeking_other"
                                        name="products_services_seeking_other"
                                        value={formData.products_services_seeking_other}
                                        onChange={handleChange}
                                        placeholder="Ej: Empaque biodegradable, desarrollo de apps móviles..."
                                        maxLength={100}
                                    />
                                    <small>{formData.products_services_seeking_other?.length || 0}/100 caracteres</small>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="section-card full-width">
                        <h3>💼 ¿Qué Productos/Servicios Puedo Ofrecer?</h3>
                        <div className="form-group">
                            <label>Selecciona los productos o servicios que puedes ofrecer en alianzas comerciales:</label>
                            <div className="checkbox-group">
                                {productsServicesOptions.map(option => (
                                    <div key={option.value} className="checkbox-item">
                                        <input
                                            type="checkbox"
                                            id={`offered_${option.value}`}
                                            checked={formData.products_services_offered.includes(option.value)}
                                            onChange={() => handleCheckboxChange('products_services_offered', option.value)}
                                        />
                                        <label htmlFor={`offered_${option.value}`}>{option.label}</label>
                                    </div>
                                ))}
                            </div>
                            {formData.products_services_offered.includes('OTROS') && (
                                <div className="form-group conditional-field">
                                    <label htmlFor="products_services_offered_other">Especifica otros productos/servicios que ofreces:</label>
                                    <input
                                        type="text"
                                        id="products_services_offered_other"
                                        name="products_services_offered_other"
                                        value={formData.products_services_offered_other}
                                        onChange={handleChange}
                                        placeholder="Ej: Logística internacional, asesoría en comercio exterior..."
                                        maxLength={100}
                                    />
                                    <small>{formData.products_services_offered_other?.length || 0}/100 caracteres</small>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ===== PASO 5: PERFIL DE ALIANZAS (TEXTO LIBRE) ===== */}
                    <div className="section-header full-width">
                        <span className="section-number">5</span>
                        <div>
                            <h2 className="section-title">🤝 ¿Qué Buscas y Qué Ofreces?</h2>
                            <p className="section-description">Describe en tus propias palabras tus necesidades y tu propuesta</p>
                        </div>
                    </div>

                    <div className="section-card">
                        <h3>🔍 ¿Qué Busco?</h3>
                        <div className="form-group">
                            <label htmlFor="what_i_seek">Describe qué necesitas encontrar</label>
                            <textarea 
                                id="what_i_seek" 
                                name="what_i_seek" 
                                value={formData.what_i_seek} 
                                onChange={handleChange} 
                                rows="5" 
                                placeholder="Ej: Busco proveedores confiables de harina de trigo y levadura. También me interesa encontrar distribuidores en Antioquia y el Eje Cafetero para expandir mi negocio. Necesito servicios de contabilidad y asesoría legal empresarial..."
                            ></textarea>
                            <small>Menciona proveedores, distribuidores, clientes potenciales o servicios que necesites</small>
                        </div>
                    </div>

                    <div className="section-card">
                        <h3>💼 ¿Qué Ofrezco?</h3>
                        <div className="form-group">
                            <label htmlFor="what_i_offer">Describe qué puedes ofrecer a otras empresas</label>
                            <textarea 
                                id="what_i_offer" 
                                name="what_i_offer" 
                                value={formData.what_i_offer} 
                                onChange={handleChange} 
                                rows="5" 
                                placeholder="Ej: Ofrezco productos de panadería artesanal de alta calidad con entrega diaria en Bogotá. Puedo proveer a restaurantes, cafeterías y tiendas. También tengo experiencia en capacitación sobre producción artesanal de pan y puedo ofrecer talleres..."
                            ></textarea>
                            <small>Menciona productos, servicios, experiencia o recursos que puedas compartir</small>
                        </div>
                    </div>

                    {/* ===== PASO 6: INFORMACIÓN ADICIONAL ===== */}
                    <div className="section-header full-width">
                        <span className="section-number">6</span>
                        <div>
                            <h2 className="section-title">📝 Información Adicional</h2>
                            <p className="section-description">Cuéntanos algo más sobre tu empresa</p>
                        </div>
                    </div>

                    <div className="section-card full-width">
                        <h3>Otros Datos Relevantes</h3>
                        <div className="form-group">
                            <label htmlFor="relevant_info">¿Algo más que quieras compartir? (Opcional)</label>
                            <textarea 
                                id="relevant_info" 
                                name="relevant_info" 
                                value={formData.relevant_info} 
                                onChange={handleChange} 
                                rows="4" 
                                placeholder="Ej: Contamos con certificaciones de calidad ISO 9001, estamos interesados en exportar nuestros productos, tenemos capacidad para pedidos grandes, somos una empresa familiar con 20 años de experiencia..."
                            ></textarea>
                            <small>Certificaciones, premios, proyectos especiales, capacidades adicionales, etc.</small>
                        </div>
                    </div>
                    
                    {/* ===== BOTÓN DE ENVÍO ===== */}
                    <div className="form-actions">
                        <button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? '⏳ Guardando...' : (companyId ? '✓ Actualizar Información' : '✓ Registrar Mi Empresa')}
                        </button>
                    </div>
                    
                    {message.content && (
                        <div className={`message ${message.type}`}>
                            {message.type === 'success' ? '✅ ' : '⚠️ '}
                            {message.content}
                        </div>
                    )}

                </form>
            </div>
        </div>
    );
};

export default CompanyProfile;

